/**
 * Message Queue System
 * Ensures no message duplication, proper ordering, and synchronization
 * Provides reliable message delivery between chatbot and live support
 */

class MessageQueue {
  constructor() {
    this.queue = [];
    this.processed = new Set();
    this.maxSize = 500;
    this.syncThreshold = 20; // Messages before auto-sync
  }

  /**
   * Add message to queue
   * @param {Object} message - Message object
   * @returns {boolean} True if added, false if duplicate
   */
  addMessage(message) {
    const messageId = this.generateMessageId(message);

    // Check for duplicates
    if (this.processed.has(messageId)) {
      console.log('[MessageQueue] Duplicate message detected:', messageId);
      return false;
    }

    // Add to queue
    this.queue.push({
      ...message,
      _messageId: messageId,
      _enqueuedAt: new Date().toISOString(),
      _status: 'pending', // pending, processing, delivered, failed
    });

    // Mark as processed
    this.processed.add(messageId);

    // Maintain queue size
    if (this.queue.length > this.maxSize) {
      const removed = this.queue.shift();
      if (removed) {
        this.processed.delete(removed._messageId);
      }
    }

    console.log('[MessageQueue] Message added:', messageId);
    return true;
  }

  /**
   * Mark message as delivered
   * @param {string} messageId - Message ID
   */
  markDelivered(messageId) {
    const msg = this.queue.find((m) => m._messageId === messageId);
    if (msg) {
      msg._status = 'delivered';
      msg._deliveredAt = new Date().toISOString();
    }
  }

  /**
   * Mark message as failed
   * @param {string} messageId - Message ID
   */
  markFailed(messageId) {
    const msg = this.queue.find((m) => m._messageId === messageId);
    if (msg) {
      msg._status = 'failed';
      msg._failedAt = new Date().toISOString();
    }
  }

  /**
   * Get messages for a specific ticket
   * @param {number} ticketId - Ticket ID
   * @returns {Array} Array of messages
   */
  getTicketMessages(ticketId) {
    return this.queue
      .filter((msg) => msg.ticketId === ticketId || msg.ticket_id === ticketId)
      .sort((a, b) => new Date(a._enqueuedAt) - new Date(b._enqueuedAt));
  }

  /**
   * Get pending messages (not yet delivered)
   * @returns {Array} Array of pending messages
   */
  getPendingMessages() {
    return this.queue.filter((msg) => msg._status === 'pending');
  }

  /**
   * Get undelivered messages for a ticket
   * @param {number} ticketId - Ticket ID
   * @returns {Array} Array of undelivered messages
   */
  getUndeliveredMessages(ticketId) {
    return this.getTicketMessages(ticketId).filter(
      (msg) => msg._status !== 'delivered'
    );
  }

  /**
   * Check if needs sync
   * @returns {boolean} True if queue size exceeds threshold
   */
  needsSync() {
    return this.getPendingMessages().length >= this.syncThreshold;
  }

  /**
   * Get queue stats
   * @returns {Object} Queue statistics
   */
  getStats() {
    return {
      totalMessages: this.queue.length,
      pendingMessages: this.getPendingMessages().length,
      uniqueProcessed: this.processed.size,
      needsSync: this.needsSync(),
      oldestMessage: this.queue.length > 0 ? this.queue[0]._enqueuedAt : null,
      newestMessage:
        this.queue.length > 0
          ? this.queue[this.queue.length - 1]._enqueuedAt
          : null,
    };
  }

  /**
   * Clear old messages (older than specified time)
   * @param {number} ageMinutes - Age in minutes
   */
  clearOldMessages(ageMinutes = 60) {
    const cutoff = new Date(Date.now() - ageMinutes * 60000);
    const before = this.queue.length;

    this.queue = this.queue.filter((msg) => {
      const msgDate = new Date(msg._enqueuedAt);
      return msgDate > cutoff;
    });

    const removed = before - this.queue.length;
    if (removed > 0) {
      console.log('[MessageQueue] Cleared', removed, 'old messages');
    }
  }

  /**
   * Clear entire queue
   */
  clear() {
    this.queue = [];
    this.processed.clear();
  }

  /**
   * Generate unique message ID
   * @private
   *
   * @param {Object} message - Message object
   * @returns {string} Unique message ID
   */
  generateMessageId(message) {
    // Use a combination of attributes to generate unique ID
    const keys = [
      message.id,
      message.message_id,
      message.ticketId,
      message.ticket_id,
      message.text,
      message.message,
      message.sender,
      message.sender_type,
      message.timestamp,
      message.created_at,
    ].filter((key) => key !== undefined && key !== null);

    // If we have an explicit ID, use it
    if (message.id || message.message_id) {
      return `${message.ticketId || message.ticket_id}-${
        message.id || message.message_id
      }`;
    }

    // Generate from content hash (simple approach)
    const content = keys.join('|');
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return `${message.ticketId || message.ticket_id}-${Math.abs(hash)}`;
  }

  /**
   * Get message by ID
   * @param {string} messageId - Message ID
   * @returns {Object|null} Message or null
   */
  getMessageById(messageId) {
    return this.queue.find((msg) => msg._messageId === messageId) || null;
  }

  /**
   * Export messages for backup
   * @returns {Array} Array of all messages
   */
  export() {
    return JSON.parse(JSON.stringify(this.queue));
  }

  /**
   * Import messages (merge with existing)
   * @param {Array} messages - Messages to import
   */
  import(messages) {
    if (!Array.isArray(messages)) return;

    messages.forEach((msg) => {
      this.addMessage(msg);
    });
  }
}

// Create singleton instances for different conversation contexts
const messageQueues = {};

/**
 * Get or create queue for a ticket
 * @param {number} ticketId - Ticket ID
 * @returns {MessageQueue} Queue instance
 */
export const getQueueForTicket = (ticketId) => {
  if (!messageQueues[ticketId]) {
    messageQueues[ticketId] = new MessageQueue();
  }
  return messageQueues[ticketId];
};

/**
 * Get all queues
 * @returns {Object} All queue instances
 */
export const getAllQueues = () => {
  return messageQueues;
};

/**
 * Clear queue for specific ticket
 * @param {number} ticketId - Ticket ID
 */
export const clearQueueForTicket = (ticketId) => {
  if (messageQueues[ticketId]) {
    messageQueues[ticketId].clear();
    delete messageQueues[ticketId];
  }
};

/**
 * Global stats across all queues
 * @returns {Object} Global statistics
 */
export const getGlobalQueueStats = () => {
  const tickets = Object.keys(messageQueues);
  const stats = {
    totalQueues: tickets.length,
    totalMessages: 0,
    totalPending: 0,
    queues: {},
  };

  tickets.forEach((ticketId) => {
    const queue = messageQueues[ticketId];
    const queueStats = queue.getStats();
    stats.totalMessages += queueStats.totalMessages;
    stats.totalPending += queueStats.pendingMessages;
    stats.queues[ticketId] = queueStats;
  });

  return stats;
};

/**
 * Cleanup all old messages across all queues
 * @param {number} ageMinutes - Age in minutes
 */
export const cleanupAllOldMessages = (ageMinutes = 60) => {
  Object.values(messageQueues).forEach((queue) => {
    queue.clearOldMessages(ageMinutes);
  });
};

export default {
  MessageQueue,
  getQueueForTicket,
  getAllQueues,
  clearQueueForTicket,
  getGlobalQueueStats,
  cleanupAllOldMessages,
};
